import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, message } from "antd";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment/moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

import classes from "./TimeSlotModal.module.scss";
import TimeSlotDetailsModal from "./TimeSlotDetailsModal";
import { fetch } from "../../store/actions/timeslots-actions";
import timeslotsAPI from "../../API/endpoints/timeslots";

const localizer = momentLocalizer(moment);

export default function TimeSlotModal({ open, onClose, formValues, zone }) {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState();
  const [selectedEvent, setSelectedEvent] = useState();
  const [selectedDate, setSelectedDate] = useState(moment());
  const [isLoading, setIsLoading] = useState();

  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    const startDate = moment(selectedDate).startOf("week").format("YYYY-MM-DD");
    const endDate = moment(selectedDate).endOf("week").format("YYYY-MM-DD");
    dispatch(fetch({ startDate, endDate, zone }));
  }, [dispatch, selectedDate, zone]);

  const earlieastAvailableTime = moment("21:59:59", "HH:mm:ss").toDate();
  const latestAvailableTime = moment(earlieastAvailableTime).add(1, "days");

  const formats = {
    timeGutterFormat: "HH:mm",
    agendaTimeFormat: "HH:mm",
    eventTimeRangeFormat: ({ start, end }, culture, localizer) => {
      const startTime = localizer.format(start, "HH:mm", culture);
      let endTime = localizer.format(end, "HH:mm", culture);

      if (endTime === "23:59") {
        endTime = "00:00";
      }

      return `${startTime} - ${endTime}`;
    },
  };

  const eventsData = useSelector((state) => state.timeslots.items) || [];
  const dates = Object.keys(eventsData);

  const eventsArray = dates.map((date) => {
    return eventsData[date].map((slot) => {
      const start = moment(
        `${date} ${slot.startTime}`,
        "YYYY-MM-DD HH:mm:ss"
      ).toDate();
      const end =
        slot.endTime === "00:00:00"
          ? moment(`${date} 23:59:00`, "YYYY-MM-DD HH:mm:ss").toDate()
          : moment(`${date} ${slot.endTime}`, "YYYY-MM-DD HH:mm:ss").toDate();
      return {
        ...slot,
        start,
        end,
        available:
          !moment(start).isBetween(
            earlieastAvailableTime,
            latestAvailableTime
          ) || slot.status === "FULL"
            ? false
            : true,
      };
    });
  });

  const events = eventsArray.flat();
  const filteredArrays = events.filter((slot) => slot.status !== "UNAVAILABLE");

  const toggleDetailsModal = () => {
    onClose();
    setIsDetailsModalOpen((prev) => !prev);
  };

  const handleOnBooked = () => {
    messageApi.open({
      type: "success",
      content: "Successfully booked",
    });
    setIsDetailsModalOpen((prev) => !prev);
    onClose("booked");
  };

  const handleSelectEvent = async (slot) => {
    if (!slot.available) return;
    try {
      setIsLoading(true);
      await timeslotsAPI.reserveSlot(slot.id);
      toggleDetailsModal();
      setSelectedEvent(slot);
    } catch (error) {
      messageApi.open({
        type: "error",
        content:
          error.response?.data?.message ||
          "This slot is full. Please select another one",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigate = (date, _) => {
    setSelectedDate(date);
  };

  const morningEvents = filteredArrays.filter((slot) =>
    moment(slot.startTime, "HH:mm").isBefore(moment("06:00", "HH:mm").toDate())
  );

  const midDayEvents = filteredArrays.filter(
    (slot) =>
      moment(slot.startTime, "HH:mm").isAfter(
        moment("05:30", "HH:mm").toDate()
      ) &&
      moment(slot.startTime, "HH:mm").isBefore(
        moment("22:00", "HH:mm").toDate()
      )
  );

  const eveningEvents = filteredArrays.filter((slot) =>
    moment(slot.startTime, "HH:mm").isAfter(moment("21:30", "HH:mm").toDate())
  );

  const eventPropGetter = useCallback(
    (event) => ({
      ...(!event.available && {
        className: "disabled",
      }),
    }),
    []
  );

  return (
    <>
      {contextHolder}
      <Modal
        title="Time Slots"
        className={classes.modal}
        open={open}
        onCancel={onClose}
        width={"80vw"}
        style={{ top: 24 }}
        styles={{ body: { height: "80vh" } }}
        footer={null}
        destroyOnClose
        loading={isLoading}
      >
        <div className={classes.zoneInfo}>
          Delivery Zone:{" "}
          <span className={classes[zone.toLowerCase()]}>{zone}</span>
        </div>
        {midDayEvents.length ? (
          <Calendar
            key={`all-events-${selectedDate}`}
            localizer={localizer}
            defaultView="week"
            views={["week"]}
            events={filteredArrays}
            timeslots={2}
            culture=""
            formats={formats}
            eventPropGetter={eventPropGetter}
            defaultDate={selectedDate}
            selectable
            onSelectEvent={handleSelectEvent}
            startAccessor="start"
            endAccessor="end"
            onNavigate={handleNavigate}
          />
        ) : (
          <div>
            <div>
              <Calendar
                key={`morning-events-${selectedDate}`}
                localizer={localizer}
                defaultView="week"
                views={["week"]}
                events={morningEvents}
                timeslots={2}
                culture=""
                formats={formats}
                eventPropGetter={eventPropGetter}
                defaultDate={selectedDate}
                selectable
                onSelectEvent={handleSelectEvent}
                startAccessor="start"
                endAccessor="end"
                onNavigate={handleNavigate}
                max={moment("05:30", "HH:mm").toDate()}
                style={{ height: "390px" }}
              />
            </div>
            <div className={`${classes.expandablePart} noHeader `}>
              <div className={`no-borders ${classes.content}`}>
                <Calendar
                  key={`midday-events-${selectedDate}`}
                  localizer={localizer}
                  defaultView="week"
                  views={["week"]}
                  toolbar={false}
                  events={midDayEvents}
                  timeslots={8}
                  step={60}
                  culture=""
                  formats={formats}
                  eventPropGetter={eventPropGetter}
                  defaultDate={selectedDate}
                  selectable
                  onSelectEvent={handleSelectEvent}
                  startAccessor="start"
                  endAccessor="end"
                  onNavigate={handleNavigate}
                  min={moment("06:00", "HH:mm").toDate()}
                  max={moment("21:30", "HH:mm").toDate()}
                />
              </div>
            </div>
            <div className="noHeader no-borders">
              <Calendar
                key={`evening-events-${selectedDate}`}
                localizer={localizer}
                defaultView="week"
                views={["week"]}
                toolbar={false}
                events={eveningEvents}
                timeslots={2}
                culture=""
                formats={formats}
                eventPropGetter={eventPropGetter}
                defaultDate={selectedDate}
                selectable
                onSelectEvent={handleSelectEvent}
                startAccessor="start"
                endAccessor="end"
                onNavigate={handleNavigate}
                min={moment("22:00", "HH:mm").toDate()}
                style={{ height: "110px" }}
              />
            </div>
          </div>
        )}
      </Modal>
      <TimeSlotDetailsModal
        open={isDetailsModalOpen}
        onClose={toggleDetailsModal}
        timeSlot={selectedEvent}
        isLoading={isLoading}
        formValues={formValues}
        onBooked={handleOnBooked}
      />
    </>
  );
}
